import { httpRequest } from '@/shared/api/client';
import type { AuthEnvelope, LoginPayload, RegisterPayload } from './types';

const ACCOUNT_BASE = '/api/account';
const ACCOUNT_MGMT_BASE = '/api/account-management';

export interface OtpVerifyPayload {
  Email: string;
  Otp: string;
  FingerPrint?: string;
  UserLang?: string;
}

// /account/* and /refresh-token are custom REST controllers but BE declares
// [Consumes("application/vnd.api+json")] on most POSTs, so we keep the default
// content-type. Logout has no body — we send `{}` to satisfy negotiation.

export async function login(payload: LoginPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/login`, { method: 'POST', body: payload, anonymous: true });
}

export async function register(payload: RegisterPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/register`, { method: 'POST', body: payload, anonymous: true });
}

export async function logout(): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/logout`, { method: 'POST', body: {} });
}

/**
 * Refresh the access token using the long-lived refresh token as Authorization.
 * BE expects `Authorization: Bearer <refreshToken>` (not the access token).
 */
export async function refreshAccessToken(refreshToken: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>('/api/refresh-token', {
    method: 'GET',
    anonymous: true,
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
}

export async function changeTenant(tenantId: string, refreshToken: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`/api/refresh-token/change-tenant?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'GET',
    anonymous: true,
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
}

export async function loginWithOtp(payload: OtpVerifyPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/login-otp`, { method: 'POST', body: payload, anonymous: true });
}

export async function registerWithOtp(payload: OtpVerifyPayload): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/registerOtp`, { method: 'POST', body: payload, anonymous: true });
}

export async function resetPasswordWithOtp(email: string, otp: string, newPassword: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/reset-password-otp`, {
    method: 'POST',
    body: { Email: email, Otp: otp, NewPassword: newPassword },
    anonymous: true,
  });
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await httpRequest(`${ACCOUNT_MGMT_BASE}/change-password`, {
    method: 'POST',
    body: { OldPassword: oldPassword, NewPassword: newPassword },
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/reset-password`, {
    method: 'POST',
    body: { Email: email },
    anonymous: true,
  });
}

export async function confirmRegistration(token: string): Promise<void> {
  await httpRequest(`${ACCOUNT_BASE}/confirmRegistration`, {
    method: 'POST',
    body: { Token: token },
    anonymous: true,
  });
}

// ─── Google login "JWT diretto" (BE v1.9+) ──────────────────────────────────
// Scambia l'id_token di Google Identity Services con il JWT della piattaforma.
// 404 = utente non registrato (va indirizzato alla registrazione).

export async function googleLoginJwt(idToken: string, fingerPrint: string, userLang?: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>(`${ACCOUNT_BASE}/google-login-jwt`, {
    method: 'POST',
    body: { idToken, fingerPrint, userLang },
    anonymous: true,
  });
}

// ─── Passkey / WebAuthn (BE v1.9+, modello stateless) ───────────────────────
// Le `begin` ritornano optionsJson (JSON WebAuthn standard) + state opaco da
// rimandare alla `finish`. login/* sono anonimi e rate-limited; register/* bearer.

export interface PasskeyBeginResponse {
  optionsJson: string;
  state: string;
}

export async function passkeyRegisterBegin(displayName?: string): Promise<PasskeyBeginResponse> {
  return httpRequest<PasskeyBeginResponse>('/api/passkey/register/begin', {
    method: 'POST',
    body: { displayName },
  });
}

export async function passkeyRegisterFinish(credentialJson: string, state: string, name?: string): Promise<void> {
  await httpRequest('/api/passkey/register/finish', {
    method: 'POST',
    body: { credentialJson, state, name },
  });
}

export async function passkeyLoginBegin(email?: string): Promise<PasskeyBeginResponse> {
  return httpRequest<PasskeyBeginResponse>('/api/passkey/login/begin', {
    method: 'POST',
    body: email ? { email } : {},
    anonymous: true,
  });
}

export async function passkeyLoginFinish(credentialJson: string, state: string, fingerPrint: string, userLang?: string): Promise<AuthEnvelope> {
  return httpRequest<AuthEnvelope>('/api/passkey/login/finish', {
    method: 'POST',
    body: { credentialJson, state, fingerPrint, userLang },
    anonymous: true,
  });
}

/** Strip "Bearer " prefix returned by BE. */
export function stripBearerPrefix(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.startsWith('Bearer ') ? raw.slice(7) : raw;
}
